import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MagicLinkPage from './pages/MagicLinkPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyPage from './pages/VerifyPage';
import DeviceVerificationPage from './pages/DeviceVerificationPage';
import OnboardingFlow from './pages/onboarding/OnboardingFlow';
import DashboardLayout from './components/layout/DashboardLayout';
import SettingsLayout from './components/layout/SettingsLayout';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import BrandSettingsPage from './pages/settings/BrandSettingsPage';
import ContentSettingsPage from './pages/settings/ContentSettingsPage';
import TaxonomySettingsPage from './pages/settings/TaxonomySettingsPage';
import WorkspaceSettingsPage from './pages/settings/WorkspaceSettingsPage';
import BillingSettingsPage from './pages/settings/BillingSettingsPage';
import ApiConnectionsSettingsPage from './pages/settings/ApiConnectionsSettingsPage';
import CreatePageRouter from './pages/create/CreatePageRouter';
import HelpMeWithIdeasPage from './pages/create/HelpMeWithIdeasPage';
import IdeasGalleryPage from './pages/create/IdeasGalleryPage';
import GenerateImagesPage from './pages/create/GenerateImagesPage';
import ApproveBoard from './pages/ApproveBoard';
import CalendarPage from './pages/CalendarPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/layout/AdminLayout';
import AdminPromptsPage from './pages/admin/AdminPromptsPage';
import AdminPromptDetailPage from './pages/admin/AdminPromptDetailPage';
import { initializeTheme } from './store/themeStore';
import { useAuthStore } from './lib/auth';

function App() {
  const { isAuthenticated, refreshWorkspace } = useAuthStore();

  // Initialize theme on mount
  useEffect(() => {
    initializeTheme();
  }, []);

  // Refresh workspace data once on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshWorkspace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        {/* Auth Routes (no layout, no workspace slug) */}
        <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/magic-link" element={<MagicLinkPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/verify-device" element={<DeviceVerificationPage />} />
      <Route path="/auth/verify-device" element={<DeviceVerificationPage />} />
      <Route path="/onboarding" element={<OnboardingFlow />} />

      {/* Dashboard Routes (workspace-scoped with /:workspaceSlug prefix) */}
      <Route path="/:workspaceSlug/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout><DashboardPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/create" element={
        <ProtectedRoute>
          <DashboardLayout><CreatePageRouter /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/create/ideas" element={
        <ProtectedRoute>
          <DashboardLayout><HelpMeWithIdeasPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/ideas" element={
        <ProtectedRoute>
          <DashboardLayout><IdeasGalleryPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/approve" element={
        <ProtectedRoute>
          <DashboardLayout><ApproveBoard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/posts/:postId/generate-images" element={
        <ProtectedRoute>
          <DashboardLayout><GenerateImagesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/calendar" element={
        <ProtectedRoute>
          <DashboardLayout><CalendarPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/statistics" element={
        <ProtectedRoute>
          <DashboardLayout><div className="p-8 dark:text-gray-100">Statistics Page (TODO)</div></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><SettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/knowledge" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><TaxonomySettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/brand" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><BrandSettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/content" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><ContentSettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/api" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><ApiConnectionsSettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/company" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><div className="p-8 dark:text-gray-100">Company Settings (TODO)</div></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/workspace" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><WorkspaceSettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/:workspaceSlug/settings/billing" element={
        <ProtectedRoute>
          <DashboardLayout><SettingsLayout><BillingSettingsPage /></SettingsLayout></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Admin Routes (no workspace scope) */}
      <Route path="/admin/prompts" element={
        <AdminRoute>
          <AdminLayout><AdminPromptsPage /></AdminLayout>
        </AdminRoute>
      } />
      <Route path="/admin/prompts/:key" element={
        <AdminRoute>
          <AdminLayout><AdminPromptDetailPage /></AdminLayout>
        </AdminRoute>
      } />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}

export default App;
